/**
 * Font Utilities for Export
 *
 * Centralised utilities for managing font compatibility in document exports.
 * Addresses the risk that custom brand fonts (Poppins, Helvetica Neue) may not
 * be available on recipients' systems when opening exported Word documents.
 *
 * IMPORTANT: There are two contexts for font safety:
 * 1. **Web app** - Bundled fonts are safe via @font-face
 * 2. **Exported documents** - Only system fonts are safe (DOCX doesn't embed fonts)
 */

/**
 * System-safe fonts that are pre-installed on Windows, macOS, and most Linux distributions.
 * These fonts will render correctly on virtually any computer without substitution.
 * These are the ONLY fonts safe for exported documents (Word, PDF, etc.).
 */
export const SYSTEM_SAFE_FONTS = ["Calibri", "Arial", "Times New Roman", "Courier New"] as const;

/**
 * Fonts bundled with this application (available via @font-face in CSS).
 * These are safe to use in the app UI but NOT safe for exported documents
 * because DOCX files don't embed fonts - they just reference font names.
 *
 * Poppins (WebuildAI brand) and Helvetica Neue (National Grid brands) are
 * bundled.
 */
export const BUNDLED_FONTS = ["Poppins", "Helvetica Neue"] as const;

/**
 * Default font for Word exports when no brand font is specified.
 * Poppins is used for brand consistency but may be substituted on some systems.
 */
export const DEFAULT_FONT = "Poppins";

/**
 * Default code font for monospace text in exports.
 * Courier New is universally available across all platforms.
 */
export const DEFAULT_CODE_FONT = "Courier New";

/**
 * Extract the primary font name from a CSS font-family string.
 * Handles comma-separated font stacks like "Poppins,sans-serif" → "Poppins"
 *
 * This is necessary because the docx library expects a single font name,
 * not a CSS font-family string with fallbacks.
 *
 * @param fontFamily - A CSS font-family string (e.g., "Poppins,sans-serif" or "'Open Sans', Arial")
 * @returns The primary (first) font name without quotes or fallbacks
 *
 * @example
 * extractPrimaryFont("Poppins,sans-serif") // "Poppins"
 * extractPrimaryFont("'Open Sans', Arial, sans-serif") // "Open Sans"
 * extractPrimaryFont("Calibri") // "Calibri"
 * extractPrimaryFont("") // "Poppins" (DEFAULT_FONT)
 */
export const extractPrimaryFont = (fontFamily: string): string => {
  if (!fontFamily) return DEFAULT_FONT;

  // Split by comma and take the first font, trim whitespace and quotes
  const primaryFont = fontFamily
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, ""); // Remove surrounding quotes

  return primaryFont || DEFAULT_FONT;
};

/**
 * Check if a font is safe for EXPORTED documents (Word, PDF, etc.).
 * Only system-installed fonts are safe - bundled web fonts will be substituted.
 *
 * Use this to determine whether to show font compatibility warnings in Export Settings.
 *
 * @param font - The font family name to check
 * @returns true if the font is system-safe (won't be substituted), false otherwise
 *
 * @example
 * isSystemSafeFont("Calibri") // true - pre-installed on most systems
 * isSystemSafeFont("Arial") // true - pre-installed on most systems
 * isSystemSafeFont("Poppins") // false - bundled in web app only, not on recipient's system
 * isSystemSafeFont("Helvetica Neue") // false - bundled in web app only
 */
export const isSystemSafeFont = (font: string): boolean => {
  if (!font) return false;

  const lowerFont = font.toLowerCase();

  // Only system-safe fonts are guaranteed to work in exported documents
  // Bundled fonts are NOT safe for export - they only work in the web app
  return SYSTEM_SAFE_FONTS.some((safe) => lowerFont.includes(safe.toLowerCase()));
};

/**
 * Get a system-safe fallback font for a given font.
 * Maps web fonts to visually similar system fonts.
 *
 * @param font - The font family name to get a fallback for
 * @returns A system-safe font that is visually similar
 *
 * @example
 * getSystemSafeFallback("Poppins") // "Calibri"
 * getSystemSafeFallback("Helvetica Neue") // "Arial"
 * getSystemSafeFallback("Arial") // "Arial" (already system-safe)
 */
export const getSystemSafeFallback = (font: string): string => {
  // If already system-safe, return as-is
  if (isSystemSafeFont(font)) return font;

  // Map web fonts to visually similar system-safe equivalents
  const fallbackMap: Record<string, string> = {
    Poppins: "Calibri",
    // Raleway is no longer bundled (it was the retired Invest Industrial brand
    // font), but the mapping stays so documents authored under it still export
    // to a sensible system font rather than the generic Calibri default.
    Raleway: "Arial",
    "Helvetica Neue": "Arial",
  };

  // Check if the font matches any known web font
  const lowerFont = font.toLowerCase();

  for (const [webFont, fallback] of Object.entries(fallbackMap)) {
    if (lowerFont.includes(webFont.toLowerCase())) {
      return fallback;
    }
  }

  // Default fallback for unknown fonts
  return "Calibri";
};
